import React, { useEffect, useState } from 'react';
import collaborativelogo from '../../../../Assets/Images/COLLABORATIVE.png';
import stratrgiclogo from '../../../../Assets/Images/STRATEGIC.png';
import visionarylogo from '../../../../Assets/Images/VISIONARY.png';
import performancelogo from '../../../../Assets/Images/PERFORMANCE.png';
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import ArrowRight from '../../../../Assets/Images/left-arrow.png';
import { applyFullTieOrder } from '../../../../utils/quadrantTieOrder';


// Image imports
import flow_CP from '../../../../Assets/Images/LeadershipFlowImg/flow_CP.png';
import flow_CS from '../../../../Assets/Images/LeadershipFlowImg/flow_CS.png';
import flow_CV from '../../../../Assets/Images/LeadershipFlowImg/flow_CV.png';
import flow_PC from '../../../../Assets/Images/LeadershipFlowImg/flow_PC.png';
import flow_PS from '../../../../Assets/Images/LeadershipFlowImg/flow_PS.png';
import flow_PV from '../../../../Assets/Images/LeadershipFlowImg/flow_PV.png';
import flow_SC from '../../../../Assets/Images/LeadershipFlowImg/flow_SC.png';
import flow_SP from '../../../../Assets/Images/LeadershipFlowImg/flow_SP.png';
import flow_SV from '../../../../Assets/Images/LeadershipFlowImg/flow_SV.png';
import flow_VC from '../../../../Assets/Images/LeadershipFlowImg/flow_VC.png';
import flow_VP from '../../../../Assets/Images/LeadershipFlowImg/flow_VP.png';
import flow_VS from '../../../../Assets/Images/LeadershipFlowImg/flow_VS.png';

const LABELS = ['strategic', 'performance', 'visionary', 'collaborative'];
const LABEL_SHORT = { strategic: 'S', performance: 'P', visionary: 'V', collaborative: 'C' };
const FLOW_IMAGES = {
  'CP': flow_CP,
  'CS': flow_CS,
  'CV': flow_CV,
  'PC': flow_PC,
  'PS': flow_PS,
  'PV': flow_PV,
  'SC': flow_SC,
  'SP': flow_SP,
  'SV': flow_SV,
  'VC': flow_VC,
  'VP': flow_VP,
  'VS': flow_VS,
};

// Flow content mapping
const FLOW_CONTENT = {
  'CS': {
    title: 'You flow from Collaborative to Strategic',
    description: 'When Collaborative Leaders transition into Strategic roles, they leverage their strong foundation in fostering teamwork and mutual respect to implement more structured and goal-oriented strategies. This shift involves harnessing the trust and communication established in their collaborative role to facilitate effective planning.',
    details: [
      'Collaborative leaders already excel in understanding and balancing team dynamics, and as they transition into strategic leadership, they begin to use these insights to define precise roles, responsibilities, and benchmarks that guide the team towards achieving specific outcomes.',
      'These leaders can effectively bridge the gap between interpersonal relationships and organizational goals. This combination ensures that strategic initiatives are not only well-planned but also broadly supported. This blended approach ensures that strategic goals are pursued with a deep understanding of and commitment to the human elements that underpin organizational success.'
    ]
  },
  'CV': {
    title: 'You flow from Collaborative to Visionary',
    description: 'When Collaborative Leaders transition into Visionary leadership, they evolve from fostering a nurturing, team-oriented environment to inspiring those individuals, and possibly the team and organization with a transformative vision. This shift involves leveraging their innate ability to connect with individuals on a personal level and to help them see beyond daily tasks to the broader impact of their work.',
    details: [
      'While their foundation remains rooted in empathy and cooperation, the scope expands dramatically—envisioning and communicating a future that not only motivates but also galvanizes the entire organization towards shared, lofty goals.',
      'This dual capacity fosters a work environment where the drive towards innovation and legacy is balanced with a deep respect for the collective journey and individual contributions. This blend of heart and soul in leadership ensures that the path to innovation and legacy is pursued with a shared commitment and a deep understanding of collective impact.'
    ]
  },
  'CP': {
    title: 'You flow from Collaborative to Performance',
    description: 'When Collaborative Leaders transition to Performance leadership, they shift their focus from solely driving high efficiency and achieving targets to emphasizing teamwork, shared goals, and collective input. Known for their ability to set clear benchmarks and drive results, these leaders begin to recognize the benefits of fostering a more inclusive and engaging team environment.',
    details: [
      'These leaders start to balance their goal-oriented approach with an appreciation for the processes that involve team dynamics and relationship-building. They may modify their leadership style to be more open and receptive to feedback, encouraging team members to contribute ideas and share in decision-making.',
      'This shift helps mitigate any tendencies they might have had to view team members as merely operational units, instead acknowledging and valuing each person\'s unique contributions and perspectives. This approach ensures that while performance remains a priority, it does not overshadow the importance of developing a supportive and flexible team environment.'
    ]
  },
  'SC': {
    title: 'You flow from Strategic to Collaborative',
    description: 'When a Strategic Leader adopts a Collaborative approach, the blend manifests as a dynamic leadership style that leverages both the mind\'s precision and the heart\'s empathy.',
    details: [
      'This leader still crafts robust structures and clear roles but does so with a heightened focus on team dynamics and individual contributions. They shift from a predominantly directive mode to facilitating open dialogues where team members feel valued and part of the decision-making process.',
      'By integrating collaborative values, such a leader enhances their strategic initiatives with increased buy-in and diversity of thought, fostering an environment where not only are the organizational goals achieved through meticulous planning and execution, but also where a culture of mutual respect and collective effort flourishes.'
    ]
  },
  'SV': {
    title: 'You flow from Strategic to Visionary',
    description: 'When a Strategic Leader combines their energy with Visionary leadership, the fusion creates a powerful style that combines meticulous planning with deep insight. This leader continues to utilize their skills in structuring and organizing, but they begin to infuse every decision with a deeper sense of purpose and commitment to the long-term vision.',
    details: [
      'They not only define roles and responsibilities within their teams but also inspire their members by connecting these roles to a larger, meaningful context. This helps transform routine tasks into parts of a grand narrative, which motivates and unites the team.',
      'This hybrid leader excels in not just setting clear, pragmatic pathways to success, but also in painting a vivid picture of what success looks like and why it matters. They adapt their methodical approach to team-building, using stories, ceremonies, and storytelling to make the organizational vision resonate with every team member, thus creating a culture that values both efficiency and inspiration.'
    ]
  },
  'SP': {
    title: 'You flow from Strategic to Performance',
    description: 'When a Strategic Leader transitions towards Performance Leadership, they harness a robust blend of methodical planning and vigorous execution. This leader still prioritizes strategic frameworks and organizational structures but shifts their focus towards optimizing performance and ensuring results swiftly.',
    details: [
      'This blend of strategic and performance leadership emphasizes efficiency and effectiveness, and not just planning the work but also working the plan. These leaders become adept at breaking down complex strategies into executable tasks, applying a pragmatic approach to realize strategic visions.',
      'They meticulously manage resources and authority to enhance productivity and customer satisfaction, ensuring the organization\'s operations are both smooth and impactful. However, their intensified focus on outcomes and efficiency may sometimes risk reducing team members to mere roles within a process, potentially overlooking individual contributions and personal growth.'
    ]
  },
  'VS': {
    title: 'You flow from Visionary to Strategic',
    description: 'When Visionary Leaders shift into Strategic roles, they begin to ground their expansive and innovative ideas into practical, actionable plans that can be systematically implemented across the organization. Initially driven by a deep sense of purpose and a compelling view of the future, these leaders evolve to focus more on the detailed steps required to turn their visions into reality.',
    details: [
      'The shift also requires Visionary Leaders to enhance their focus on data and metrics. While they continue to inspire with their ideas, they also begin to emphasize the importance of performance indicators, risk assessments, and market analyses to inform their decisions.',
      'This dual focus ensures that their visionary projects are not only inspiring but also viable and aligned with the organization\'s broader goals. Furthermore, as they transition into Strategic leadership, these leaders are required to balance innovation with risk management. They maintain their inspirational edge but bolster it with a pragmatic focus on execution and sustainability.'
    ]
  },
  'VC': {
    title: 'You flow from Visionary to Collaborative',
    description: 'When Visionary Leaders transition to their Collaborative energy, they meld their grand, innovative ideas with a more inclusive and team-oriented approach. They begin to emphasize the importance of collective input and teamwork for realizing that vision.',
    details: [
      'They recognize that integrating varied insights can enrich their ideas or vision and make implementation more effective and universally acceptable. They want others to feel empowered by their contributions and participation in shaping the direction.',
      'A leader may create platforms for open communication where ideas are freely exchanged and developed collectively. This approach not only enhances team cohesion but also ensures that each member feels a personal stake in the success of the collective endeavor.'
    ]
  },
  'VP': {
    title: 'You flow from Visionary to Performance',
    description: 'When Visionary Leaders transition to Performance leadership, they start to align their broad, forward-thinking visions with the focused pursuit of concrete results. Known for their ability to see beyond the present and inspire with grand ideas, these leaders want to show them.',
    details: [
      'Visionary leaders moving into a performance focus also refine their ability to communicate the practical steps necessary to achieve their vision. They are likely to be adept at setting clear, challenging, yet achievable goals, and motivating their team to meet these through precise actions and disciplined follow-through.',
      'Moreover, these leaders learn to balance their enthusiasm for big-picture thinking with a focus on the day-to-day processes that drive success. These leaders have the opportunity to bridge the gap between dreaming big and acting decisively. This combination of high-level inspiration and ground-level execution can be creatively practical, a rare trait found in most organizations.'
    ]
  },
  'PC': {
    title: 'You flow from Performance to Collaborative',
    description: 'When Performance Leaders transition to include Collaborative leadership, they shift their focus from solely driving high efficiency and achieving targets to emphasizing teamwork, shared goals, and collective input. Known for their ability to set clear benchmarks and drive results, these leaders begin to recognize the benefits of fostering a more inclusive and engaging team environment.',
    details: [
      'These leaders start to balance their goal-oriented approach with an appreciation for the processes that involve team dynamics and relationship-building. They may modify their leadership style to be more open and receptive to feedback, encouraging team members to contribute ideas and share in decision-making.',
      'This shift helps mitigate any tendencies they might have had to view team members as merely operational units, instead acknowledging and valuing each person\'s unique contributions and perspectives. This approach ensures that while performance remains a priority, it does not overshadow the importance of developing a supportive and flexible team environment.'
    ]
  },
  'PV': {
    title: 'You flow from Performance to Visionary',
    description: 'When Performance Leaders integrate Visionary leadership, these leaders start to harness their drive and drive to fuel more transformative ambitions that require not just action but foresight and innovation. They begin to see beyond the present and performance indicators, focusing instead on how these can serve larger, more impactful goals.',
    details: [
      'This shift often involves embracing more risk and ambiguity than they are accustomed to, as visionary goals typically require venturing into uncharted territories. They learn to articulate a clear and motivating picture of the future, linking everyday tasks to these larger objectives, which infuses team\'s work with deeper meaning and purpose.',
      'They may begin to prioritize innovation and creativity in their strategy execution. They encourage their teams to think outside the box and challenge the status quo, fostering an environment where new ideas are valued and explored. Ultimately, they evolve from managers who excel in driving efficiency to leaders who inspire change.'
    ]
  },
  'PS': {
    title: 'You flow from Performance to Strategic',
    description: 'When Performance Leaders transition to Strategic roles, they start to channel their results-driven focus and exceptional execution capabilities towards broader, long-term planning and organizational structuring.',
    details: [
      'They know how to get things done, but they now start to think in terms of systems and processes that are scalable and sustainable, extending their operational excellence to encompass strategic foresight. This transition also demands a new approach to leadership that balances their focus on short-term results with the patience required for strategic planning.',
      'They learn to articulate not just what needs to be done but why it matters in the broader scheme of the organization\'s future, thereby securing buy-in and commitment from their teams and stakeholders. Ultimately these leaders evolve from being highly effective managers of tasks to architects of the organization\'s future.'
    ]
  }
};

function getFlowImageName(scores) {
  const maxIdx = scores.indexOf(Math.max(...scores));
  const temp = [...scores];
  temp[maxIdx] = -Infinity;
  const secondIdx = temp.indexOf(Math.max(...temp));
  const from = LABEL_SHORT[LABELS[maxIdx]];
  const to = LABEL_SHORT[LABELS[secondIdx]];
  return `${from}${to}`;
}

const leadershipShort = {
  strategic: 'S',
  performance: 'P',
  visionary: 'V',
  collaborative: 'C'
};

function getLeadershipFlowImage(leadershipSnapshotResults, leadershipLabels) {
  if (!leadershipSnapshotResults || !leadershipLabels || leadershipSnapshotResults.length !== 4 || leadershipLabels.length !== 4) return null;
  const pairs = leadershipLabels.map((label, i) => ({
    label: label.toLowerCase(),
    score: leadershipSnapshotResults[i]
  }));
  const sorted = [...pairs].sort((a, b) => b.score - a.score);
  const highest = sorted[0].label;
  const secondHighest = sorted[1].label;
  const flowKey = `${leadershipShort[highest]}${leadershipShort[secondHighest]}`;
  return FLOW_IMAGES[flowKey];
}

function getFlowContent(leadershipSnapshotResults, leadershipLabels) {
  if (!leadershipSnapshotResults || !leadershipLabels || leadershipSnapshotResults.length !== 4 || leadershipLabels.length !== 4) {
    return FLOW_CONTENT['CS']; // Default fallback
  }

  const pairs = leadershipLabels.map((label, i) => ({
    label: label.toLowerCase(),
    score: leadershipSnapshotResults[i]
  }));
  const sorted = [...pairs].sort((a, b) => b.score - a.score);
  const highest = sorted[0].label;
  const secondHighest = sorted[1].label;
  const flowKey = `${leadershipShort[highest]}${leadershipShort[secondHighest]}`;

  return FLOW_CONTENT[flowKey] || FLOW_CONTENT['CS']; // Fallback to CS if not found
}

// Self-report version of the Leadership Flow Diagram (copied UI from group report)
const BOX_WIDTH = 108.81;
const BOX_HEIGHT = 38.63;
const ARROW_WIDTH = 70;
const BOX_RADIUS = 16;
const BOX_PADDING = '6px 8px 3px 8px';
const LABEL_FONT = {
  fontFamily: 'Lato, sans-serif',
  fontWeight: 700,
  fontSize: 18,
  color: '#245775',
  marginTop: 12,
  textAlign: 'center',
};
const PERCENT_FONT = {
  fontFamily: 'Lato, sans-serif',
  fontWeight: 400,
  fontSize: 18,
  color: '#5795B2',
  marginTop: 2,
  textAlign: 'center',
};
const START_FONT = {
  fontFamily: 'Lato, sans-serif',
  fontWeight: 700,
  fontSize: 11,
  color: '#245775',
  marginBottom: 6,
  textAlign: 'center',
};
const COLORS = {
  'collaborative': '#D04B2F',
  'strategic': '#7A8793',
  'visionary': '#2B5C73',
  'performance': '#C89B2B'
};

const LeadershipFlow2 = ({ apiData, pageNumber }) => {
  const [scoreRanking, setScoreRanking] = useState([])
  const [variablsMapArray, setVariableMapArray] = useState()


  useEffect(() => {
    const variableMap = apiData?.variables?.reduce((acc, el) => {
      acc[el.variable] = el.score;
      return acc;
    }, {});

    setVariableMapArray(variableMap)
  }, [apiData])


  useEffect(() => {

    let highestScore = -Infinity;
    let highestVariable = "";
    let results = [];
    let labelsArray = [];

    if (variablsMapArray) {
      // console.log(variablsMapArray);
      const performance_r1_m = variablsMapArray["per_round1_m"] || 0;
      const strategic_r1_m = variablsMapArray["strat_round1_m"] || 0;
      const visionary_r1_m = variablsMapArray["vis_round1_m"] || 0;
      const collaborative_r1_m = variablsMapArray["col_round1_m"] || 0;

      const performance_r2_m = variablsMapArray["per_round2_m"] || 0;
      const strategic_r2_m = variablsMapArray["strat_round2_m"] || 0;
      const visionary_r2_m = variablsMapArray["vis_round2_m"] || 0;
      const collaborative_r2_m = variablsMapArray["col_round2_m"] || 0;

      const performance_r1_l = variablsMapArray["per_round1_l"] || 0;
      const strategic_r1_l = variablsMapArray["strat_round1_l"] || 0;
      const visionary_r1_l = variablsMapArray["vis_round1_l"] || 0;
      const collaborative_r1_l = variablsMapArray["col_round1_l"] || 0;

      const performance_r2_l = variablsMapArray["per_round2_l"] || 0;
      const strategic_r2_l = variablsMapArray["strat_round2_l"] || 0;
      const visionary_r2_l = variablsMapArray["vis_round2_l"] || 0;
      const collaborative_r2_l = variablsMapArray["col_round2_l"] || 0;


      const collaborative_highest = Math.max(collaborative_r1_m, collaborative_r2_m, collaborative_r1_l, collaborative_r2_l);
      const strategic_highest = Math.max(strategic_r1_m, strategic_r2_m, strategic_r1_l, strategic_r2_l);
      const performance_highest = Math.max(performance_r1_m, performance_r2_m, performance_r1_l, performance_r2_l);
      const visionary_highest = Math.max(visionary_r1_m, visionary_r2_m, visionary_r1_l, visionary_r2_l);


      let most_score = Math.max(performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m)
      let second_most_score = Math.max(performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m) / 2
      let least_score = Math.max(performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l)
      let second_least_score = Math.max(performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l) / 2

      if (least_score >= second_least_score) {
        least_score = (second_least_score - 1)
      }

      const total = most_score + second_most_score + least_score + second_least_score;

      const most_per = (most_score / total) * 100;
      const second_most_per = (second_most_score / total) * 100;
      const least_per = (least_score / total) * 100;
      const second_least_per = (second_least_score / total) * 100;

      const labels_arr = ["performance", "strategic", "visionary", "collaborative"];

      const mostValues = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m];
      const secondMostValues = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m];
      const leastValues = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l];
      const secondLeastValues = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l];

      const most_score_label = Math.max(...mostValues);
      const second_most_score_label = Math.max(...secondMostValues);
      const least_score_label = Math.max(...leastValues);
      const second_least_score_label = Math.max(...secondLeastValues);

      const getLabel = (values, maxValue) => labels_arr[values.indexOf(maxValue)];

      const most_label = getLabel(mostValues, most_score_label);
      const second_most_label = getLabel(secondMostValues, second_most_score_label);
      const least_label = getLabel(leastValues, least_score_label);
      const second_least_label = getLabel(secondLeastValues, second_least_score_label);

      const getPercentageOrder = (label) => {
        if (label === most_label?.toLowerCase()) return most_per;
        if (label === second_most_label?.toLowerCase()) return second_most_per;
        if (label === least_label?.toLowerCase()) return least_per;
        if (label === second_least_label?.toLowerCase()) return second_least_per;
        return 0;
      };

      const RankedScores = [
        { name: "Collaborative", value: parseFloat(getPercentageOrder("collaborative").toFixed(2)) },
        { name: "Strategic", value: parseFloat(getPercentageOrder("strategic").toFixed(2)) },
        { name: "Performance", value: parseFloat(getPercentageOrder("performance").toFixed(2)) },
        { name: "Visionary", value: parseFloat(getPercentageOrder("visionary").toFixed(2)) }
      ];

      setScoreRanking(RankedScores);

      const sortedVariables = [
        { name: "collaborative", value: getPercentageOrder("collaborative") },
        { name: "strategic", value: getPercentageOrder("strategic") },
        { name: "performance", value: getPercentageOrder("performance") },
        { name: "visionary", value: getPercentageOrder("visionary") }
      ].sort((a, b) => b.value - a.value);

      results = sortedVariables.map(item => item.value);
      labelsArray = sortedVariables.map(item => item.name);


    }
  }, [variablsMapArray]);


  const Arrow = () => (
    <svg width="70" height="28" viewBox="0 0 70 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
      <line x1="0" y1="12" x2="70" y2="12" stroke="#245775" strokeWidth="3.6" />
      <path d="M30 3L40 12L30 21" fill="none" stroke="#245775" strokeWidth="3.6" />
    </svg>

  );


  const SquareWithArrow = () => {

    const leadershipScores = {
      strategic: scoreRanking.find(item => item.name.toLowerCase() === 'strategic')?.value || 0,
      performance: scoreRanking.find(item => item.name.toLowerCase() === 'performance')?.value || 0,
      visionary: scoreRanking.find(item => item.name.toLowerCase() === 'visionary')?.value || 0,
      collaborative: scoreRanking.find(item => item.name.toLowerCase() === 'collaborative')?.value || 0
    };

    const sortedLeadership = applyFullTieOrder(
      Object.entries(leadershipScores).sort((a, b) => b[1] - a[1]),
      ([name]) => name,
      ([, value]) => value
    )
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: Math.round(value)
      }));

    const diagramSize = 206;
    const headLength = 14;
    const headWidth = 8;
    const centerOffsetX = diagramSize / 2;
    const centerOffsetY = diagramSize / 2;
    const quadrantOffsetX = centerOffsetX / 2;
    const quadrantOffsetY = centerOffsetY / 2;
    const quadrantCenters = {
      strategic: { x: quadrantOffsetX, y: quadrantOffsetY },
      performance: { x: diagramSize - quadrantOffsetX, y: quadrantOffsetY },
      visionary: { x: quadrantOffsetX, y: diagramSize - quadrantOffsetY },
      collaborative: { x: diagramSize - quadrantOffsetX, y: diagramSize - quadrantOffsetY }
    };

    const pathPoints = sortedLeadership.map(step => quadrantCenters[step.name.toLowerCase()]);

    let arrowPath = '';
    if (pathPoints.length > 0) {
      const pointsForPath = [...pathPoints];
      if (pointsForPath.length > 1) {
        const tip = pointsForPath[pointsForPath.length - 1];
        const prev = pointsForPath[pointsForPath.length - 2];
        const dx = tip.x - prev.x;
        const dy = tip.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const trimmedTip = {
          x: tip.x - (headLength * dx) / len,
          y: tip.y - (headLength * dy) / len
        };
        pointsForPath[pointsForPath.length - 1] = trimmedTip;
      }

      arrowPath = `M ${pointsForPath[0].x} ${pointsForPath[0].y}`;
      for (let i = 1; i < pointsForPath.length; i++) {
        arrowPath += ` L ${pointsForPath[i].x} ${pointsForPath[i].y}`;
      }
    }

    const dotPosition = sortedLeadership[0] ?
      quadrantCenters[sortedLeadership[0].name.toLowerCase()] :
      { x: 0, y: 0 };

    const arrowColor = '#B6111F';
    let arrowHeadPoints = '';



    if (sortedLeadership.length > 1) {
      const lastIdx = sortedLeadership.length - 1;
      const tip = quadrantCenters[sortedLeadership[lastIdx].name.toLowerCase()];
      const prev = quadrantCenters[sortedLeadership[lastIdx - 1].name.toLowerCase()];
      const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
      const left = {
        x: tip.x - headLength * Math.cos(angle) + headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) - headWidth * Math.cos(angle)
      };
      const right = {
        x: tip.x - headLength * Math.cos(angle) - headWidth * Math.sin(angle),
        y: tip.y - headLength * Math.sin(angle) + headWidth * Math.cos(angle)
      };
      arrowHeadPoints = `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
    }

    return (
      <div style={{ position: 'relative', width: `${diagramSize}px`, height: `${diagramSize}px`, backgroundColor: 'transparent', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid #000000' }}>
        <div style={{ position: 'absolute', width: '2px', height: '100%', backgroundColor: '#000', left: '50%', transform: 'translateX(-50%)' }}></div>
        <div style={{ position: 'absolute', width: '100%', height: '2px', backgroundColor: '#000', top: '50%', transform: 'translateY(-50%)' }}></div>

        <div style={{ position: 'absolute', top: '5px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>STRATEGIC</div>
        <div style={{ position: 'absolute', top: '20px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.strategic === 'number' ? leadershipScores.strategic.toFixed(2) : '0.00'}%
        </div>

        <div style={{ position: 'absolute', top: '5px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>PERFORMANCE</div>
        <div style={{ position: 'absolute', top: '20px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.performance === 'number' ? leadershipScores.performance.toFixed(2) : '0.00'}%
        </div>

        <div style={{ position: 'absolute', bottom: '5px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>VISIONARY</div>
        <div style={{ position: 'absolute', bottom: '20px', left: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.visionary === 'number' ? leadershipScores.visionary.toFixed(2) : '0.00'}%
        </div>

        <div style={{ position: 'absolute', bottom: '5px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>COLLABORATIVE</div>
        <div style={{ position: 'absolute', bottom: '20px', right: '10px', color: '#000', fontFamily: 'Helvetica', fontWeight: 700, fontStyle: 'normal', fontSize: '10px', lineHeight: '14px', letterSpacing: '0px', leadingTrim: 'none' }}>
          {typeof leadershipScores.collaborative === 'number' ? leadershipScores.collaborative.toFixed(2) : '0.00'}%
        </div>


        <svg width={diagramSize} height={diagramSize} style={{ position: 'absolute', top: 0, left: 0 }}>
          <circle cx={dotPosition.x} cy={dotPosition.y} r="8" fill={arrowColor} />
          <path
            d={arrowPath}
            stroke={arrowColor}
            strokeWidth="4"
            strokeOpacity="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {arrowHeadPoints && (
            <polygon points={arrowHeadPoints} fill={arrowColor} stroke={arrowColor} />
          )}
        </svg>

      </div>
    );
  };


  const imagesMap = {
    Collaborative: { icon: collaborativelogo, label: "Collaborative" },
    Strategic: { icon: stratrgiclogo, label: "Strategic" },
    Visionary: { icon: visionarylogo, label: "Visionary" },
    Performance: { icon: performancelogo, label: "Performance" },
  };

  const sortedScores = applyFullTieOrder([...scoreRanking].sort((a, b) => b.value - a.value));

  const sortedImages = sortedScores?.map(item => imagesMap[item.name]);


  const baseSize = 88;
  const sizeDecrement = 30;
  const sizeSequence = [88, 64, 50, 28];
  const minSize = sizeSequence[sizeSequence.length - 1];
  const imageSizes = sortedImages.map((_, index) =>
    sizeSequence[index] || Math.max(minSize, baseSize - index * sizeDecrement)
  );
  const itemWidth = Math.max(...imageSizes) + 20;

  return (
    <div>
      <HeaderPreview apiData={apiData} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "64px 0px" }}>
        {SquareWithArrow()}
      </div>
      <div style={{ width: '100%', paddingTop: "34px" }}>
        <div style={{
          position: "relative",
          paddingTop: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "411px",
          margin: "0px auto"
        }}>
          <p style={{
            position: "absolute",
            left: "10%",
            transform: "translateX(-50%)",
            top: "0",
            fontSize: '11px',
            fontWeight: 700,
            color: '#245775',
            fontFamily: 'Helvetica',
            margin: 0,
            marginBottom: '8px'
          }}>Start</p>
          <div style={{
            width: "100%",
            maxWidth: "411px",
            display: 'flex',
            flexDirection: "row",
            gap: "57px",
            alignItems: "center",
            justifyContent: "center"
          }}>
            {sortedImages?.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                width: `${itemWidth}px`,
              }}>
                <img
                  src={item?.icon || ""}
                  alt={item?.label}
                  style={{ width: `${imageSizes[index]}px`, height: `${imageSizes[index]}px`, zIndex: "9999" }}
                />

                {index < sortedImages.length - 1 && (
                  <div style={{
                    position: "absolute",
                    top: `${imageSizes[index] / 2}px`,
                    left: index === 0
                      ? "87px"
                      : index === 1
                        ? "62.5px"
                        : index === 2
                          ? "49px"
                          : "0px",
                    width: index == 2
                      ? "82px" : "74px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <span style={{
                      position: "absolute",
                      fontSize: 50,
                      fontWeight: 300,
                      top: -30,
                      left: 0,
                      color: "#245775",
                      fontFamily: 'Helvetica'
                    }}>
                      <Arrow />
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{
            width: "100%",
            display: 'flex',
            flexDirection: "row",
            gap: "60px",
            alignItems: "center",
            justifyContent: "start"
          }}>
            {sortedImages.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                zIndex: "999",
                width: `${imageSizes[index]}px`
              }}>
                <div style={{
                  marginTop: '16px',
                  textAlign: 'center',
                  width: `${imageSizes[index]}px`,
                  minWidth: "fit-content",
                }}>
                  <p style={{
                    margin: 0,
                    lineHeight: '18px',
                    fontWeight: 700,
                    fontSize: '11px',
                    color: '#245775',
                    fontFamily: 'Helvetica'
                  }}>{item.label}</p>
                  <p style={{
                    margin: 0,
                    fontSize: '11px',
                    color: '#5795B2',
                    fontWeight: 400,
                    textAlign: 'center',
                    lineHeight: '18px',
                    fontFamily: 'Helvetica'
                  }}> {(parseFloat(sortedScores[index].value) || 0).toFixed(2)}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} />
    </div>
  )
}

export default LeadershipFlow2;
